import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { pageContentApi, type PageContent } from "@/integrations/mysql/client";

export type { PageContent };

export function usePageContent(page: string) {
  const queryClient = useQueryClient();
  const apiPage = page === 'blog_page' ? 'blog-page' : page;

  const { data: content = [], isLoading: loading } = useQuery({
    queryKey: ['page-content', page],
    queryFn: () => pageContentApi.list(apiPage),
    staleTime: 30_000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PageContent> }) =>
      pageContentApi.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['page-content', page] }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<PageContent, 'id'>) => pageContentApi.create(apiPage, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['page-content', page] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pageContentApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['page-content', page] }),
  });

  const get = (section: string, key: string): PageContent | undefined =>
    content.find((c) => c.section === section && c.content_key === key);

  const getText = (section: string, key: string, fallback = ''): string =>
    (get(section, key)?.content_value?.text as string) ?? fallback;

  const upsert = async (
    section: string,
    key: string,
    value: Record<string, unknown>,
    displayOrder = 0,
  ) => {
    const existing = get(section, key);
    if (existing) {
      await updateMutation.mutateAsync({ id: existing.id, data: { content_value: value } });
    } else {
      await createMutation.mutateAsync({
        page,
        section,
        content_key: key,
        content_value: value,
        is_active: true,
        display_order: displayOrder,
      });
    }
  };

  return {
    content,
    loading,
    get,
    getText,
    upsert,
    updateContent: updateMutation.mutateAsync,
    createContent: createMutation.mutateAsync,
    deleteContent: deleteMutation.mutateAsync,
    isUpdating: updateMutation.isPending || createMutation.isPending,
  };
}
